import Proptypes from "prop-types";
import { flatMap } from "lodash";
import { Alert } from "react-bootstrap";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import Typography from "@shared/v2/Typography";
import RadioButton from "./RadioButton";

const ManageAccessModal = ({
  people,
  admins,
  agents,
  modalOpen,
  closeModal,
  radioGroupHandler,
  handleSubmit,
  submitResponse,
  saving,
}) => (
  <div>
    <Modal
      show={modalOpen}
      onHide={closeModal}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[680px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      <Modal.Header
        title={`Manage Access to ${people.length > 1 ? `${people.length} People` : "Person"}`}
        closeButton
      />

      <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-gap-[24px] tw-overflow-auto">
        {submitResponse?.errors && (
          <Alert bsStyle="danger">
            {flatMap(submitResponse.errors, (field, key) => (
              <span>
                <strong>Error: </strong> {`${key} ${field[0]}`}
                <br />
              </span>
            ))}
          </Alert>
        )}
        {submitResponse?.content && (
          <Alert bsStyle="danger">
            <span>
              <strong>Error: </strong> {submitResponse.content}
              <br />
            </span>
          </Alert>
        )}
        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <Typography>
            Extending access gives a user the ability to view and edit a person&apos;s information. Access can
            be revoked only for teammates with the user type of agent. The account owner and admins have
            access to everyone.
          </Typography>
          <Typography>You can only manage access for up to 50 people at a time.</Typography>
        </div>
        <div className="tw-flex">
          <div className="tw-basis-2/12">
            <label htmlFor="Primary">Primary</label>
          </div>
          <div className="tw-basis-5/12">
            <label htmlFor="Name">Name</label>
          </div>
          <div className="tw-basis-5/12">
            <label htmlFor="Access">Access</label>
          </div>
        </div>

        <div className="tw-flex tw-flex-col">
          {admins.map((admin) => (
            <div key={admin.person_id} className="tw-flex agent">
              <div className="tw-basis-2/12 primary-agent-container text-center">
                <RadioButton
                  name={admin.person_id}
                  isAdmin
                  isPond={false}
                  selected={admin.is_selected}
                  radioGroupHandler={radioGroupHandler}
                />
              </div>
              <div className="tw-basis-5/12">{admin.name}</div>
              <div className="tw-basis-5/12 access-level">
                <span className="tw-text-[#969696] tw-text-[0.9em]">Admin access</span>
              </div>
            </div>
          ))}

          <hr />

          {agents.map((agent) => (
            <div key={agent.id || agent.person_id} className="tw-flex agent">
              <div className="tw-basis-2/12 primary-agent-container text-center">
                <RadioButton
                  name={agent.id || agent.person_id}
                  isAdmin={false}
                  isPond={agent?.id && true}
                  selected={agent.is_selected}
                  radioGroupHandler={radioGroupHandler}
                />
              </div>
              <div className="tw-basis-5/12">{agent.name}</div>
              <div className="tw-basis-5/12 tw-p-0">
                {agent?.person_id && (
                  <div className="tw-flex tw-gap-[8px]">
                    <div className="tw-basis-3/12">
                      <RadioButton
                        name={agent.person_id}
                        isAdmin={false}
                        isPond={false}
                        label="None"
                        selected={agent.current_access_level === "none"}
                        radioGroupHandler={radioGroupHandler}
                      />
                    </div>
                    {(agent.current_access_level === "some" || agent.issome) && (
                      <div className="tw-basis-5/12">
                        <RadioButton
                          name={agent.person_id}
                          isPond={false}
                          isAdmin={false}
                          label={`Some (${agent.some_access_count})`}
                          selected={agent.current_access_level === "some"}
                          radioGroupHandler={radioGroupHandler}
                        />
                      </div>
                    )}
                    <div className="tw-basis-3/12">
                      <RadioButton
                        name={agent.person_id}
                        isAdmin={false}
                        isPond={false}
                        label="All"
                        selected={agent.current_access_level === "all"}
                        radioGroupHandler={radioGroupHandler}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        <Button size="medium" schema="tertiary" onClick={() => closeModal()}>
          Close
        </Button>
        <Button size="medium" isLoading={saving} onClick={() => handleSubmit()}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  </div>
);

ManageAccessModal.propTypes = {
  people: Proptypes.arrayOf(
    Proptypes.shape({
      person_id: Proptypes.number,
    }),
  ),
  admins: Proptypes.arrayOf(
    Proptypes.shape({
      person_id: Proptypes.number,
    }),
  ),
  agents: Proptypes.arrayOf(
    Proptypes.shape({
      name: Proptypes.string,
    }),
  ),
  modalOpen: Proptypes.bool,
  closeModal: Proptypes.func,
  radioGroupHandler: Proptypes.func,
  handleSubmit: Proptypes.func,
  submitResponse: Proptypes.shape({ content: Proptypes.string, errors: Proptypes.arrayOf(Proptypes.string) }),
  saving: Proptypes.bool,
};

ManageAccessModal.defaultProps = {
  people: null,
  admins: null,
  agents: null,
  modalOpen: false,
  closeModal: null,
  radioGroupHandler: null,
  handleSubmit: null,
  submitResponse: null,
  saving: false,
};

export default ManageAccessModal;
